import React, { useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import { formatFigure } from 'Helpers/formatService';

import Tags from 'CommonComponents/Tags';
import Tooltip from 'CommonComponents/Tooltip';

import CircleCheckIcon from 'Assets/icons/circle-check.svg';
import EditIcon from 'Assets/icons/edit.svg';
import StarFilledIcon from 'Assets/icons/star-filled.svg';
import StarIcon from 'Assets/icons/star.svg';
import TrashIcon from 'Assets/icons/trash.svg';

export default function GeneralIssuesTable(props) {
    const [issues, setIssues] = useState([]);
    const issuesListRef = useRef();

    const columnsCount = useMemo(() => {
        const count = 10;
        if (props.showSourceColumn) {
            return count + 1;
        }
        if (props.impactCategories) {
            return count + props.impactCategories.length;
        }
        return count;
    }, [props.showSourceColumn, props.impactCategories]);

    useEffect(() => {
        setIssues(props.issues);
    }, [props.issues]);

    useEffect(() => {
        if (issuesListRef.current) {
            issuesListRef.current.scrollTop = issuesListRef.current.scrollHeight;
        }
    }, [props.issues.length]);

    function handleOnDragEnd(result) {
        if (!result.destination) {
            return;
        }

        const newIssues = [...issues];
        const [reorderedIssues] = newIssues.splice(result.source.index, 1);
        newIssues.splice(result.destination.index, 0, reorderedIssues);
        setIssues(newIssues);

        axios.post(props.reorderUrl, {
            issues: newIssues.map((issue) => issue.id),
        });
    }

    return (
        <div className="inbud-table_wrapper">
            <DragDropContext onDragEnd={handleOnDragEnd}>
                <Droppable droppableId="issuesList">
                    {(provided) => (
                        <div className={'tw-overflow-y-auto tw-max-h-[60vh]'} ref={provided.innerRef} {...provided.droppableProps}>
                            <table className="inbud-table tw-table-fixed issuesList">
                                <thead>
                                    <tr>
                                        <th className="tw-sticky tw-top-0 tw-left-0 tw-bg-gray-100 tw-w-32 tw-z-10">{$t('Reference')}</th>
                                        {props.showSourceColumn && (
                                            <th className="tw-sticky tw-top-0 tw-left-32 tw-bg-gray-100 tw-w-64 tw-z-10">{$t('Source')}</th>
                                        )}

                                        <th
                                            className={classNames('tw-sticky tw-top-0 tw-bg-gray-100 tw-w-96 tw-z-10', {
                                                'tw-left-96': props.showSourceColumn,
                                                'tw-left-32': !props.showSourceColumn,
                                            })}
                                        >
                                            {$t('Description')}
                                        </th>
                                        <th className="tw-sticky tw-top-0 tw-w-32">{$t('Owner')}</th>
                                        <th className="tw-sticky tw-top-0 tw-w-32">{$t('Status')}</th>
                                        <th className="tw-sticky tw-top-0 tw-w-16">{$t('Priority')}</th>
                                        <th className="tw-sticky tw-top-0 tw-w-32">{$t('No. of Actions')}</th>
                                        <th className="tw-sticky tw-top-0 tw-w-32">{$t('Actions - Completed')}</th>
                                        <th className="tw-sticky tw-top-0 tw-w-32">{$t('Actions - Outstanding')}</th>
                                        <th className="tw-sticky tw-top-0 tw-w-32 tw-capitalize">{$t('Resolution date')}</th>
                                        {props.impactCategories?.map((impactCategory) => (
                                            <th key={impactCategory?.id} className="tw-sticky tw-top-0 tw-w-32">
                                                {impactCategory.title}
                                            </th>
                                        ))}
                                        <th className="tw-sticky tw-top-0 tw-w-32">{$t('Actions')}</th>
                                    </tr>
                                </thead>
                                {!props.isLoading && !!issues?.length && (
                                    <tbody>
                                        {issues.map((issue, index) => (
                                            <Draggable key={issue.id} draggableId={issue.id.toString()} index={index}>
                                                {(provided) => (
                                                    <GeneralIssueRow
                                                        key={issue.id}
                                                        showSourceColumn={props.showSourceColumn}
                                                        issue={issue}
                                                        impactCategories={props.impactCategories}
                                                        showUrl={props.showUrl}
                                                        onStar={props.onStar}
                                                        onEdit={props.onEdit}
                                                        onDelete={props.onDelete}
                                                        canManage={props.canManage}
                                                        provided={provided}
                                                    />
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </tbody>
                                )}
                                {props.isLoading && (
                                    <tbody>
                                        <tr>
                                            <td colSpan={columnsCount}>{$t('Loading')}...</td>
                                        </tr>
                                    </tbody>
                                )}
                                {!props.isLoading && !issues?.length && (
                                    <tbody>
                                        <tr>
                                            <td colSpan={columnsCount}>
                                                <span className="tw-italic">{$t('No Issues found')}</span>
                                            </td>
                                        </tr>
                                    </tbody>
                                )}
                            </table>
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </div>
    );
}

function GeneralIssueRow({ showSourceColumn, issue, impactCategories, showUrl, onStar, onEdit, onDelete, provided, ...props }) {
    const sourceNames = useMemo(() => {
        if (!issue.source_names) {
            return [$t('Central')];
        }
        try {
            return JSON.parse(issue.source_names);
        } catch (e) {
            console.error('Error parsing source names for issue ID ' + issue.id, e);
            return null;
        }
    }, [issue.source_names]);

    return (
        <tr ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
            <td className={'tw-sticky tw-left-0 tw-z-5'}>{issue.id}</td>
            {showSourceColumn && <td className="tw-sticky tw-left-32 tw-z-5">{<Tags tags={sourceNames} />}</td>}
            <td className={classNames('tw-sticky tw-z-5', { 'tw-left-96': showSourceColumn, 'tw-left-32': !showSourceColumn })}>
                <Tooltip placement="auto" tooltipContent={issue.detailed_description ? issue.detailed_description : '-'} tooltipType="text">
                    <div className="tw-flex tw-space-x-1 tw-w-fit">
                        <div className="tw-text-blue">{issue.description}</div>
                    </div>
                </Tooltip>
            </td>
            <td>
                {issue.owner?.first_name} {issue.owner?.last_name}
            </td>
            <td>{issue.status?.title}</td>
            <td className="tw-capitalize">{issue.priority}</td>
            <td className="inbud-numeric-cell">{formatFigure(issue.actions_count?.total, String)}</td>
            <td className="inbud-numeric-cell">{formatFigure(issue.actions_count?.completed, String)}</td>
            <td className="inbud-numeric-cell">{formatFigure(issue.actions_count?.outstanding, String)}</td>
            <td className="inbud-date-cell">{issue.resolution_date}</td>
            {impactCategories?.map((impactCategory) => (
                <td key={impactCategory?.id}>
                    <div className="tw-flex tw-items-center tw-justify-center">
                        {issue.impact_category_ids?.includes(impactCategory?.id) && <CircleCheckIcon className="tw-w-5 tw-h-5 tw-text-green" />}
                    </div>
                </td>
            ))}
            <td>
                <div className="tw-flex tw-items-center tw-gap-x-3">
                    <button type="button" onClick={() => onStar(issue)}>
                        {issue.is_hot ? <StarFilledIcon className="tw-w-3 tw-h-3 tw-text-blue" /> : <StarIcon className="tw-w-3 tw-h-3" />}
                    </button>
                    {!!props.canManage && (
                        <>
                            <button type="button" onClick={() => onEdit(issue)}>
                                <EditIcon className="tw-w-3 tw-h-3" />
                            </button>
                            <button type="button" className="tw-text-red" onClick={() => onDelete(issue)}>
                                <TrashIcon className="tw-w-3 tw-h-3" />
                            </button>
                        </>
                    )}
                </div>
            </td>
        </tr>
    );
}
